import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ModalLayout from '../layouts/ModalLayout'
import { modalPrimaryButtonStyle } from '../styles/buttonStyles'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import ReceiptDetailModal from './ReceiptDetailModal'
import TableSortLabel from '@mui/material/TableSortLabel'
import ResetTableButton from '../components/ResetTableButton'
import GenericListFilter from '../components/GenericListFilter'
import FilterToggleButton from '../components/FilterToggleButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import ExportButton from '../components/ExportButton'
import { useAuth } from '../context/AuthContext'
import {
  buildReceiptExportRecords,
  exportReceiptsCSV,
  exportReceiptsXLS,
  exportReceiptsPDF,
  exportReceiptsXML,
  exportReceiptsICS,
  exportReceiptsJSON
} from '../utils/receiptExport'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const receiptDetailFields = [
  ['date', 'TRIPLOG.RECEIPTS.TABLE.DATE'],
  ['vehicleId', 'TRIPLOG.RECEIPTS.TABLE.VEHICLE'],
  ['spz', 'vehicle_license_plate'],
['fuel_type', 'TRIPLOG.RECEIPTS.TABLE.FUEL_TYPE'],
['charging_type', 'TRIPLOG.RECEIPTS.TABLE.CHARGING_TYPE'],
  ['fuel_name', 'TRIPLOG.RECEIPTS.TABLE.FUEL_NAME'],
  ['unit_price', 'TRIPLOG.RECEIPTS.TABLE.UNIT_PRICE'],
  ['units', 'TRIPLOG.RECEIPTS.TABLE.UNITS'],
  ['unit', 'TRIPLOG.RECEIPTS.TABLE.UNIT'],
  ['total_price', 'TRIPLOG.RECEIPTS.TABLE.TOTAL_PRICE'],
  ['currency', 'TRIPLOG.RECEIPTS.TABLE.CURRENCY'],
  ['vat_rate', 'TRIPLOG.RECEIPTS.TABLE.VAT_RATE'],
  ['receipt_number', 'TRIPLOG.RECEIPTS.TABLE.RECEIPT_NUMBER'],
  ['uid', 'TRIPLOG.RECEIPTS.TABLE.UID'],
  ['payment_type', 'TRIPLOG.RECEIPTS.TABLE.PAYMENT_TYPE'],
  ['transaction_id', 'TRIPLOG.RECEIPTS.TABLE.TRANSACTION_ID'],
  ['store_name', 'TRIPLOG.RECEIPTS.TABLE.STORE_NAME'],
  ['store_address', 'TRIPLOG.RECEIPTS.TABLE.STORE_ADDRESS'],
  ['ico', 'TRIPLOG.RECEIPTS.TABLE.ICO'],
  ['dic', 'TRIPLOG.RECEIPTS.TABLE.DIC'],
  ['note', 'TRIPLOG.RECEIPTS.TABLE.NOTES'],
  ['bonus_amount', 'TRIPLOG.RECEIPTS.TABLE.BONUS_AMOUNT'],
]


const formatBonusAmount = (receipt) => {
  const raw = receipt?.bonus_amount
  const numeric = Number(raw)

  if (raw == null || raw === '' || Number.isNaN(numeric)) return '-'

  const currency = receipt?.currency || 'EUR'
  return `${numeric.toFixed(2)} ${currency}`
}

const formatReceiptUnits = (value) => {
  const numeric = Number(value)
  if (value == null || value === '' || Number.isNaN(numeric)) return '-'
  return numeric.toFixed(2)
}

const getReceiptFieldLabel = (t, labelKey) => t(labelKey)

const RECEIPT_MODAL_COLUMNS_KEY = 'receiptModalColumns'
const RECEIPT_MODAL_COL_WIDTHS_KEY = 'receiptModalColWidths'

function SortableHeaderCell({ col, orderBy, order, onSort, colWidths, onResize }) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.key })
  const customWidth = colWidths[col.key]

  return (
    <th
      ref={setNodeRef}
      style={{
        ...thStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        ...(customWidth ? { width: customWidth, minWidth: customWidth, maxWidth: customWidth } : { minWidth: 120 }),
        position: 'relative',
        paddingRight: 8,
        boxSizing: 'border-box'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: 6, color: '#999' }}>⠿</span>
        <TableSortLabel
          active={orderBy === col.key}
          direction={orderBy === col.key ? order : 'asc'}
          onClick={() => onSort(col.key)}
        >
          {getReceiptFieldLabel(t, col.labelKey)}
        </TableSortLabel>
      </div>
      <div style={{ position: 'absolute', right: 4, top: '20%', bottom: '20%', width: 1, background: '#9e9e9e' }} />
      <div onMouseDown={(e) => onResize(e, col.key)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize' }} />
    </th>
  )
}

const ReceiptListModal = ({
  open,
  openExportDropdown,
  onClose,
  receipts = [],
  filterCriteria,
  setFilterCriteria
}) => {
  const { t } = useTranslation()
const { user } = useAuth()
const isLoggedIn = Boolean(user)
const membershipId = user?.membership_type_id ?? null
const limitedMembership = membershipId === 1 || membershipId === 4
const fullMembership = !limitedMembership
  const [viewport, setViewport] = useState(window.innerWidth)
  const [modalSize, setModalSize] = useState('fullscreen')
  const [hoveredReceipt, setHoveredReceipt] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
  const [tooltipEnabled, setTooltipEnabled] = useState(() => {
  const stored = localStorage.getItem('tooltipEnabled')
  return stored === null ? true : stored === 'true'
})

useEffect(() => {
  localStorage.setItem('tooltipEnabled', tooltipEnabled)
}, [tooltipEnabled])  

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

const [selectedReceipt, setSelectedReceipt] = useState(null)
const { showSearch, setShowSearch, showFilter, setShowFilter } = useSharedModalUiState()
const [searchQuery, setSearchQuery] = useState('')

const [receiptDetailOpen, setReceiptDetailOpen] = useState(false)
const [forceOpenExport, setForceOpenExport] = useState(false)
const [orderBy, setOrderBy] = useState(() => localStorage.getItem('receiptOrderBy') || 'date')
const [order, setOrder] = useState(() => localStorage.getItem('receiptOrder') || 'desc')
const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem(RECEIPT_MODAL_COLUMNS_KEY)
  if (stored) return JSON.parse(stored)

  return receiptDetailFields.map(([key, labelKey]) => ({
    key,
    labelKey
  }))
})
const [colWidths, setColWidths] = useState(() => {
  const stored = localStorage.getItem(RECEIPT_MODAL_COL_WIDTHS_KEY)
  return stored ? JSON.parse(stored) : {}
})
const sensors = useSensors(useSensor(PointerSensor))

  useEffect(() => {
    localStorage.setItem(RECEIPT_MODAL_COLUMNS_KEY, JSON.stringify(columns))
  }, [columns])

  useEffect(() => {
    localStorage.setItem(RECEIPT_MODAL_COL_WIDTHS_KEY, JSON.stringify(colWidths))
  }, [colWidths])

  useEffect(() => {
    localStorage.setItem('receiptOrderBy', orderBy)
  }, [orderBy])

  useEffect(() => {
    localStorage.setItem('receiptOrder', order)
  }, [order])

useEffect(() => {
  if (open && openExportDropdown) {
    setForceOpenExport(true)

    setTimeout(() => {
      setForceOpenExport(false)
    }, 0)
  }
}, [open, openExportDropdown])

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(col)
      setOrder('asc')
    }
  }

  const handleMouseDown = (e, key) => {
    const startX = e.clientX
    const startWidth = colWidths[key] || 150

    const onMouseMove = (event) => {
      const delta = event.clientX - startX
      const newWidth = Math.max(80, startWidth + delta)
      setColWidths(prev => ({ ...prev, [key]: newWidth }))
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = columns.findIndex(c => c.key === active.id)
    const newIndex = columns.findIndex(c => c.key === over.id)
    const reordered = [...columns]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    setColumns(reordered)
  }

  useEffect(() => {
    const handleResize = () => setViewport(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (open) setModalSize('fullscreen')
  }, [open])

  useEffect(() => {
    const loadVehicles = async () => {
      if (!receipts.length) return

      const userId = receipts[0]?.userId
      if (!userId) return

      try {
        const res = await fetchWithAuth(`/users/${userId}/vehicles`)
        if (!res.ok) return

        const data = await res.json()
        if (!Array.isArray(data)) return

        const index = data.reduce((acc, vehicle) => {
          if (vehicle?.id == null) return acc
          acc[String(vehicle.id)] = vehicle
          return acc
        }, {})

        setVehiclesById(index)
      } catch {}
    }

    loadVehicles()
  }, [receipts])

const getVehicleLabel = (r) => {
  const vehicleFromList = vehiclesById[String(r?.vehicleId)]
  const vehicle = vehicleFromList || r.vehicle

  return (
    vehicle?.name ||
    vehicle?.model ||
    vehicle?.licensePlate ||
    vehicle?.spz ||
    '-'
  )
}

const getVehicleSpz = (r) => {
  const vehicleFromList = vehiclesById[String(r?.vehicleId)]
  const vehicle = vehicleFromList || r.vehicle

  return vehicle?.licensePlate || vehicle?.spz || '-'
}

const getReceiptSearchData = (receipt) => {
  const vehicleFromList = vehiclesById[String(receipt?.vehicleId)]
  const vehicle = vehicleFromList || receipt?.vehicle

  return {
    ...receipt,
    vehicleName: vehicle?.name,
    vehicleModel: vehicle?.model,
    vehicleLicensePlate: vehicle?.licensePlate,
    vehicleSpz: vehicle?.spz,
    searchDate: receipt?.date
      ? new Date(receipt.date).toLocaleDateString('sk-SK')
      : ''
  }
}

const filteredReceipts = receipts.filter((receipt) => {
  const itemDate = receipt?.date ? receipt.date.slice(0, 10) : null

  if (filterCriteria?.vehicleId && String(receipt.vehicleId) !== String(filterCriteria.vehicleId)) return false
  if (filterCriteria?.from && itemDate < filterCriteria.from) return false
  if (filterCriteria?.to && itemDate > filterCriteria.to) return false
  if (filterCriteria?.month && !itemDate?.startsWith(filterCriteria.month)) return false
  if (filterCriteria?.year && !itemDate?.startsWith(filterCriteria.year)) return false

  return true
})

const searchFilteredReceipts = useGenericTextSearch(
  filteredReceipts,
  searchQuery,
  getReceiptSearchData
)

const sortedReceipts = [...searchFilteredReceipts].sort((a, b) => {
  let aVal = a[orderBy]
  let bVal = b[orderBy]

  if (orderBy === 'vehicleId') {
    aVal = getVehicleLabel(a)
    bVal = getVehicleLabel(b)
  }

if (orderBy === 'spz') {
  aVal = getVehicleSpz(a)
  bVal = getVehicleSpz(b)
}

  if (orderBy === 'date') {
    aVal = aVal ? new Date(aVal).getTime() : 0
    bVal = bVal ? new Date(bVal).getTime() : 0
  }

  if (['unit_price', 'units', 'total_price', 'vat_rate'].includes(orderBy)) {
    aVal = Number(aVal) || 0
    bVal = Number(bVal) || 0
  }

  if (typeof aVal === 'string') aVal = aVal.toLowerCase()
  if (typeof bVal === 'string') bVal = bVal.toLowerCase()

  if (aVal > bVal) return order === 'asc' ? 1 : -1
  if (aVal < bVal) return order === 'asc' ? -1 : 1
  return 0
})

const exportRecords = buildReceiptExportRecords(
  sortedReceipts,
  vehiclesById,
  t
)

const exportHeaders = columns
  .map(c => c.key)
  .filter(key => key !== 'bonus_amount')

const selectedReceiptIndex = sortedReceipts.findIndex(
  (item) => item?.id === selectedReceipt?.id
)

const goToPrevReceipt = () => {
  if (selectedReceiptIndex <= 0) return
  const prevReceipt = sortedReceipts[selectedReceiptIndex - 1]
  setSelectedReceipt(prevReceipt)
  setActiveRow(prevReceipt?.id ?? null)
}

const goToNextReceipt = () => {
  if (selectedReceiptIndex < 0 || selectedReceiptIndex >= sortedReceipts.length - 1) return
  const nextReceipt = sortedReceipts[selectedReceiptIndex + 1]
  setSelectedReceipt(nextReceipt)
  setActiveRow(nextReceipt?.id ?? null)
}

const formatHeaderDate = (d) => {
  if (!d) return ''
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return d

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}

let periodText = t('TRIPLOG.STATISTICS.ALL_TIME')

if (filterCriteria?.month) {
  const [year, month] = filterCriteria.month.split('-')
  periodText = `${month}-${year}`
} else if (filterCriteria?.year) {
  periodText = filterCriteria.year
} else if (filterCriteria?.from || filterCriteria?.to) {
  const from = formatHeaderDate(filterCriteria.from)
  const to = formatHeaderDate(filterCriteria.to)
  periodText = `${from} - ${to}`
}

let vehicleText = t('TRIPLOG.BUTTON.ALL_VEHICLES')

if (filterCriteria?.vehicleId) {
  const v = vehiclesById[String(filterCriteria.vehicleId)]

  if (v) {
    const name = v.name || v.model || ''
    const plate = v.licensePlate || v.spz || ''

    vehicleText = plate ? `${name} (${plate})` : name
  }
}

  if (!open) return null

  return (
    <ModalLayout open={open} onClose={onClose} size={modalSize} closeOnEscape={!receiptDetailOpen}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

        <div
          style={{
            position: 'absolute',
            top: -10,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8
  }}
>

<GenericSearchToggle
  visible={showSearch}
  query={searchQuery}
  onQueryChange={setSearchQuery}
  onToggle={() => {
    setShowSearch(prev => {
      if (prev) setSearchQuery('')
      return !prev
    })
  }}
  placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
/>

<FilterToggleButton
  visible={showFilter}
active={Object.values(filterCriteria || {}).some(v => v && v !== '')}
  onToggle={() => setShowFilter(prev => !prev)}
/>

<label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
              fontSize: 13,
              userSelect: 'none',
	      marginTop: 5
            }}
          >
            <input
              type="checkbox"
              checked={tooltipEnabled}
              onChange={(e) => setTooltipEnabled(e.target.checked)}
              style={{
                cursor: 'pointer',
                appearance: 'none',
                width: 16,
                height: 16,
                border: '1px solid #1e3f44',
                borderRadius: 3,
                background: tooltipEnabled ? '#06857a' : '#fff'
              }}
            />
            {t('TRIPLOG.SERVICECOSTS.TOGGLE')}
          </label>
<ResetTableButton
  isModified={
    Object.keys(colWidths).length > 0 ||
    JSON.stringify(columns.map(c => c.key)) !==
      JSON.stringify(receiptDetailFields.map(([key]) => key)) ||
    orderBy !== 'date' ||
    order !== 'desc'
  }
  onReset={() => {
const resetCols = receiptDetailFields.map(([key, labelKey]) => ({
  key,
  labelKey
}))

    setColumns(resetCols)
    setColWidths({})
    setOrderBy('date')
    setOrder('desc')

    localStorage.removeItem(RECEIPT_MODAL_COLUMNS_KEY)
    localStorage.removeItem(RECEIPT_MODAL_COL_WIDTHS_KEY)
    localStorage.removeItem('receiptOrderBy')
    localStorage.removeItem('receiptOrder')
  }}
/>
<ExportButton
  forceOpen={forceOpenExport}
  onCSV={() => exportReceiptsCSV(exportRecords, exportHeaders, t, periodText, vehicleText)}
  onXLS={() => exportReceiptsXLS(exportRecords, exportHeaders, t, periodText, vehicleText)}
  onPDF={() => exportReceiptsPDF(exportRecords, exportHeaders, t, periodText, vehicleText)}
  onXML={() => exportReceiptsXML(exportRecords, exportHeaders)}
  onICS={() => exportReceiptsICS(exportRecords)}
  onJSON={() => exportReceiptsJSON(exportRecords)}
  isLoggedIn={isLoggedIn}
  canUsePremiumFormats={isLoggedIn && fullMembership}
/>
          <button
            onClick={() =>
              setModalSize(prev => prev === 'fullscreen' ? 'max' : 'fullscreen')
            }
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 32
            }}
          >
            ⛶
          </button>
        </div>

        <h3 style={{ fontSize: 20, marginTop: 0, marginBottom: 10 }}>
          {t('TRIPLOG.TAB.RECEIPTS')}
        </h3>
{showFilter && (
  <GenericListFilter
    value={filterCriteria}
    onChange={setFilterCriteria}
vehicles={Object.values(vehiclesById).map(v => {
  const name = v.name || v.model || v.licensePlate || v.spz || `Vozidlo #${v.id}`
  const plate = v.licensePlate || v.spz || ''

  return {
    value: String(v.id),
    label: plate && name !== plate ? `${name} (${plate})` : name
  }
})}
    labels={{
      allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
      from: t('TRIPLOG.FILTER_FROM'),
      to: t('TRIPLOG.FILTER_TO'),
      month: t('TRIPLOG.FILTER_MONTH'),
      year: t('TRIPLOG.FILTER_YEAR'),
      clear: t('TRIPLOG.BUTTON.CLEAR')
    }}
  />
)}
<div
  style={{
    border: '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    overflow: 'auto',
    minHeight: 0
  }}
>
          <table
            style={{
              borderCollapse: 'collapse',
              width: 'max-content',
              minWidth: '100%',
              fontSize: 12,
              whiteSpace: 'nowrap'
            }}
          >
            <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
              <tr style={{ background: '#c8e1e1', fontWeight: 700 }}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={columns.map(c => c.key)} strategy={horizontalListSortingStrategy}>
                    {columns.map((col) => (
                      <SortableHeaderCell
                        key={col.key}
                        col={col}
                        orderBy={orderBy}
                        order={order}
                        onSort={handleSort}
                        colWidths={colWidths}
                        onResize={handleMouseDown}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </tr>
            </thead>

            <tbody>
              {receipts.length === 0 ? (
                <tr>
                  <td colSpan={receiptDetailFields.length} style={{ padding: 16 }}>
                    {t('TRIPLOG.RECEIPTS.EMPTY')}
                  </td>
                </tr>
              ) : (
sortedReceipts.map(r => {
  const isManual = r.manual === true

  return (
    <tr
      key={r.id}
      style={{
        borderTop: '1px solid #ececec',
        borderLeft: isManual ? '3px solid #06857a' : '3px solid transparent',
        backgroundColor:
          activeRow === r.id
            ? '#e6f4f1'
            : hoveredReceipt?.id === r.id
              ? '#f3fafa'
              : isManual
                ? '#f6ffff'
                : '#fff',
        cursor: 'pointer'
      }}
      onClick={() => {
        setActiveRow(r.id)
        setSelectedReceipt(r)
        setReceiptDetailOpen(true)
      }}
      onMouseEnter={(e) => {
        if (e.target.closest('.vehicle-detail-button')) return
        setHoveredReceipt(r)
      }}
      onMouseMove={(e) => {
        if (e.target.closest('.vehicle-detail-button')) {
          setHoveredReceipt(null)
        } else {
          setHoveredReceipt(r)
        }
      }}
      onMouseLeave={(e) => {
        const related = e.relatedTarget
        if (related && related.closest && related.closest('.vehicle-tooltip')) return
        setHoveredReceipt(null)
      }}
    >
{columns.map(({ key }) => (
  <td key={key} style={tdStyle}>
    {key === 'vehicleId'
      ? getVehicleLabel(r)
      : key === 'spz'
        ? getVehicleSpz(r)
        : key === 'date'
          ? (r.date ? (
              <>
                <div>{new Date(r.date).toLocaleDateString('sk-SK')}</div>
                <div>{new Date(r.date).toLocaleTimeString('sk-SK')}</div>
              </>
            ) : '-')
          : key === 'store_name' && r.store_name === 'manual-entry'
            ? '-'
            : key === 'payment_type' && r.payment_type === 'manual'
              ? '-'
              : key === 'fuel_type'
                ? r.fuel_type === 'electric'
                  ? t('TRIPLOG.RECEIPTS.TABLE.ELECTRIC')
                  : r.fuel_type === 'fuel'
                    ? t('TRIPLOG.RECEIPTS.TABLE.FUEL')
                    : '-'
                : key === 'charging_type'
                  ? r.fuel_type !== 'electric'
                    ? '-'
                    : r.charging_type === 'public'
                      ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC')
                      : r.charging_type === 'home'
                        ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME')
                        : r.charging_type === 'package'
                          ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE')
                          : '-'
                  : key === 'bonus_amount'
                    ? formatBonusAmount(r)
                    : key === 'units'
                      ? formatReceiptUnits(r.units)
                      : r[key] ?? '-'}
  </td>
))}
    </tr>
  )
})
              )}
            </tbody>
          </table>
        </div>

          {tooltipEnabled && hoveredReceipt && (
            <div
              className="vehicle-tooltip"
              onMouseLeave={() => setHoveredReceipt(null)}
              style={{
                position: 'fixed',
                right:
                  viewport >= 2400 ? 69 :
                  viewport >= 1920 ? 51 :
                  viewport >= 1600 ? 40 :
                  viewport >= 1366 ? 38 : 20,
                bottom:
                  viewport >= 1920 ? 120 :
                  viewport >= 1600 ? 110 :
                  viewport >= 1366 ? 105 : 30,
                fontSize:
                  viewport >= 1920 ? 14 :
                  viewport >= 1600 ? 14 :
                  viewport >= 1366 ? 13 : 13,
                maxHeight:
                  viewport >= 1920 ? '80vh' :
                  viewport >= 1600 ? '75vh' : '96vh',
                background: '#f3fafa',
                border: '1px solid #1e3f44',
                borderRadius: 8,
                padding: '16px 20px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                zIndex: 9999,
                overflowY: 'auto'
              }}
            >
{receiptDetailFields
  .filter(([key]) =>
    key !== 'bonus_amount' &&
    !(
      (key === 'fuel_type' || key === 'charging_type') &&
      hoveredReceipt?.fuel_type !== 'electric'
    )
  )
  .map(([key, labelKey]) => (
    <div key={key} style={{ marginBottom: 6, display: 'flex' }}>
      <strong style={{ minWidth: 140 }}>
        {getReceiptFieldLabel(t, labelKey)}:
      </strong>

      <span style={{ color: '#1e3f44', fontWeight: 600 }}>
        {key === 'vehicleId'
          ? getVehicleLabel(hoveredReceipt)
          : key === 'spz'
            ? getVehicleSpz(hoveredReceipt)
            : key === 'date'
              ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>
                      {hoveredReceipt.date
                        ? `${new Date(hoveredReceipt.date).toLocaleDateString('sk-SK')} | ${new Date(hoveredReceipt.date).toLocaleTimeString('sk-SK')}`
                        : '-'}
                    </span>

                    {hoveredReceipt?.manual === true && (
                      <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                        ({t('TRIPLOG.RECEIPTS.MANUAL_LABEL')})
                      </span>
                    )}
                  </span>
                )
              : key === 'store_name' && hoveredReceipt.store_name === 'manual-entry'
                ? '-'
                : key === 'payment_type' && hoveredReceipt.payment_type === 'manual'
                  ? '-'
                  : key === 'fuel_type'
                    ? t('TRIPLOG.RECEIPTS.TABLE.ELECTRIC')
                    : key === 'charging_type'
                      ? hoveredReceipt.charging_type === 'public'
                        ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC')
                        : hoveredReceipt.charging_type === 'home'
                          ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME')
                          : hoveredReceipt.charging_type === 'package'
                            ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE')
                            : '-'
                      : key === 'units'
                        ? formatReceiptUnits(hoveredReceipt.units)
                        : hoveredReceipt[key] ?? '-'}
      </span>
    </div>
  ))}
            {hoveredReceipt?.bonus_paid === true && (
              <div style={{ marginBottom: 6, display: 'flex' }}>
                <strong style={{ minWidth: 140 }}>
                  {t('TRIPLOG.RECEIPTS.TABLE.BONUS_AMOUNT', { defaultValue: 'Výška bonusu' })}:
                </strong>
                <span style={{ color: '#1e3f44', fontWeight: 600 }}>
                  {formatBonusAmount(hoveredReceipt)}
                </span>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 15, display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={onClose}
            onMouseEnter={() => setHoverBtn('close')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('close')}
            onMouseUp={() => setPressedBtn(null)}
            style={modalPrimaryButtonStyle(
              pressedBtn,
              'close',
              hoverBtn === 'close'
            )}
          >
            {t('common.close')}
          </button>
        </div>

      </div>
<ReceiptDetailModal
  open={receiptDetailOpen}
  onClose={() => {
    setReceiptDetailOpen(false)
    setSelectedReceipt(null)
    setActiveRow(null)
  }}
  receipt={selectedReceipt}
  onPrevReceipt={goToPrevReceipt}
  onNextReceipt={goToNextReceipt}
  hasPrevReceipt={selectedReceiptIndex > 0}
  hasNextReceipt={selectedReceiptIndex >= 0 && selectedReceiptIndex < sortedReceipts.length - 1}
/>
    </ModalLayout>
  )
}

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  borderBottom: '1px solid #c7c7c7'
}

const tdStyle = {
  padding: '4px 12px'
}

export default ReceiptListModal